import {
  Modal,
  IconButton,
  Button,
  ButtonSize,
  ButtonVariant,
  Input,
  IconButtonSize
} from "shared/components";
import { FC, useState } from "react";
import { XClose, User01 } from "untitledui-js-base";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Invitee } from "shared/types";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { ApplicantRole } from "../../../../../applicant/common/components/ApplicantRole";

interface Props {
  onClose: () => void;
  pendingMagicLink: Invitee;
  applicationId: string;
  onSuccess?: () => void;
}

const useResendInviteMutation = (
  email: string,
  pendingMagicLink: Invitee,
  applicationId: string,
  onSuccess?: () => void
) =>
  useMutation({
    mutationFn: () =>
      instance.post(`/portal/v1/applications/${applicationId}/resend_invite`, {
        pending_magic_link_id: pendingMagicLink.id,
        new_email: email
      }),
    onSuccess: () => {
      toast.success(
        `Invite was successfully sent to ${email || pendingMagicLink.email}`
      );
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(`Error resending invite: ${getErrorMessage(error)}`);
    }
  });

export const ResendInviteModal: FC<Props> = ({
  onClose,
  onSuccess,
  pendingMagicLink,
  applicationId
}) => {
  const [email, setEmail] = useState(pendingMagicLink.email || "");
  const { mutate: resendInvite, isPending } = useResendInviteMutation(
    email,
    pendingMagicLink,
    applicationId,
    onSuccess
  );

  const handleCopyLink = async (applicationLink: string) => {
    try {
      await navigator.clipboard.writeText(applicationLink);
      toast.info("Link copied");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Modal
      opened={Boolean(pendingMagicLink)}
      onClose={onClose}
      panelClassName="max-w-[600px] w-full flex flex-col"
    >
      <div className="p-6 border-b border-alpha-40 relative">
        <h1 className="text-xl font-bold text-content-1000 mb-2">
          Edit email and resend invitation
        </h1>
        <p className="text-sm text-content-400">
          You can send an email to the new address or resend it to the existing
          one
        </p>
        <IconButton
          Icon={XClose}
          size={IconButtonSize.SM}
          borderLess
          className="absolute top-4 right-4"
          onClick={onClose}
        />
      </div>
      <div className="px-6 py-5 bg-white">
        <div className="flex gap-4">
          <div className="w-12 h-12 rounded-full bg-primary-20 flex items-center justify-center shrink-0">
            <User01 className="w-5 h-5 stroke-primary-500" />
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-content-700">
              {pendingMagicLink.email}
            </p>
            <p className="text-sm text-content-400 capitalize">
              <ApplicantRole role={pendingMagicLink.role} />
            </p>
          </div>
        </div>
      </div>
      <div className="p-6 bg-background-900">
        <Input
          id="resend-invite-email"
          label="Email"
          placeholder="johndoe@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          readOnly={isPending}
          required
        />
      </div>
      <div className="flex justify-between h-[72px] px-6 py-4 border-t border-alpha-100">
        <Button
          disabled={!pendingMagicLink.application_link}
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={(e) => {
            e.stopPropagation();
            handleCopyLink(pendingMagicLink.application_link ?? "");
          }}
        >
          Copy applicant’s link
        </Button>

        <div className="flex gap-2 justify-end">
          <Button
            className="w-[103px]"
            variant={ButtonVariant.BASE}
            onClick={() => onClose()}
            size={ButtonSize.SM}
          >
            Cancel
          </Button>
          <Button
            className="w-[103px]"
            variant={ButtonVariant.PRIMARY}
            onClick={() => resendInvite()}
            loading={isPending}
            size={ButtonSize.SM}
            disabled={isPending}
          >
            Resend invite
          </Button>
        </div>
      </div>
    </Modal>
  );
};
